import {Injectable, OnDestroy, signal} from '@angular/core';

/**
 * Dictée : on enregistre le micro, on rend un fichier nommé.
 * Fourni par le panneau de chat — le flux micro se ferme avec lui.
 */
@Injectable()
export class AiVoiceService implements OnDestroy {

  readonly isRecording = signal(false);

  /** Micro refusé ou indisponible : le panneau le dit, il n'y a rien à réessayer sans geste utilisateur. */
  readonly isUnavailable = signal(false);

  /** Durée écoulée : sans repère, on ne sait pas si le micro écoute encore. */
  readonly elapsedSeconds = signal(0);

  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private isStarting = false;
  private timer: ReturnType<typeof setInterval> | null = null;

  ngOnDestroy(): void {
    this.stopTimer();
    this.releaseStream();
  }

  async start(): Promise<void> {
    // Deux clics rapprochés ouvriraient deux flux : le premier serait remplacé sans être fermé,
    // et l'indicateur micro du navigateur resterait allumé jusqu'à la fermeture de l'onglet.
    if (this.stream || this.isStarting) return;

    this.isStarting = true;
    this.isUnavailable.set(false);

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({audio: true});
    } catch {
      this.isUnavailable.set(true);
      return;
    } finally {
      this.isStarting = false;
    }

    const mimeType = AiVoiceService.pickMimeType();
    this.chunks = [];
    this.recorder = new MediaRecorder(this.stream, mimeType ? {mimeType} : undefined);
    this.recorder.ondataavailable = event => {
      if (event.data.size > 0) this.chunks.push(event.data);
    };
    this.recorder.start();
    this.isRecording.set(true);

    this.elapsedSeconds.set(0);
    this.timer = setInterval(() => this.elapsedSeconds.update(seconds => seconds + 1), 1000);
  }

  /** Rend null si rien n'a été capté — micro coupé, ou clic trop bref. */
  async stop(): Promise<File | null> {
    const recorder = this.recorder;
    if (!recorder) return null;

    const stopped = new Promise<void>(resolve => recorder.addEventListener('stop', () => resolve(), {once: true}));
    recorder.stop();
    await stopped;

    this.isRecording.set(false);
    this.stopTimer();
    this.releaseStream();
    this.recorder = null;

    if (this.chunks.length === 0) return null;

    const type = recorder.mimeType || 'audio/webm';
    const blob = new Blob(this.chunks, {type});
    this.chunks = [];

    if (blob.size === 0) return null;

    // Le client généré poste le Blob sans nom : sans extension, le service de transcription
    // n'a plus que le type MIME pour reconnaître le format. On lui donne les deux.
    return new File([blob], `dictation.${AiVoiceService.extensionOf(type)}`, {type});
  }

  private stopTimer(): void {
    if (this.timer !== null) { clearInterval(this.timer); }

    this.timer = null;
  }

  private releaseStream(): void {
    this.stream?.getTracks().forEach(track => track.stop());
    this.stream = null;
  }

  // Ogg/Opus est le format le plus largement accepté côté transcription ; webm est le défaut de Chrome.
  private static pickMimeType(): string | undefined {
    const candidates = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

    return candidates.find(type => MediaRecorder.isTypeSupported(type));
  }

  private static extensionOf(mimeType: string): string {
    if (mimeType.includes('ogg')) return 'ogg';
    if (mimeType.includes('webm')) return 'webm';
    if (mimeType.includes('mp4')) return 'mp4';
    if (mimeType.includes('wav')) return 'wav';

    return 'bin';
  }
}
